import React, { useState, useEffect } from "react";
import {
  Gift, Filter, Globe, X
} from "lucide-react";
import { Card } from "../../../components/ui/card";
import { Button } from "../../../components/ui/Button";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../redux/store";
import {
  fetchLatestPromotionSetting,
  fetchPromotionFormSettings,
  createPromotionSettingAction,
  updatePromotionSettingAction,
  bulkCreatePromotionFormSettingsAction,
  bulkUpdatePromotionFormSettingsAction,
  deletePromotionFormSettingAction,
  clearPromotionSettingsError,
  clearPromotionFormSettingsError
} from "../../../redux/Slices/PromotionSettingSlice";
import {
  PromotionFormSettingCreateDto,
  PromotionFormSettingUpdateDto
} from "../../../modules/shared/model/promotion-settings.interfaces";
import MultiSelectCategoryDropdown from "../../../components/ui/MultiSelectCategoryDropdown";
import MultiSelectAdminRegionDropdown from "../../../components/ui/MultiSelectAdminRegionDropdown";

interface Category {
  id: number;
  name: string;
  status: string;
  source: string;
  formCount: number;
  lastSync: string;
  region: string;
}

interface Region {
  id: string;
  name: string;
  active: boolean;
}

interface DropdownCategory {
  id: string;
  name: string;
  subcategories?: string[];
}

interface DropdownRegion {
  id: string;
  name: string;
  country?: string;
  state?: string;
  city?: string;
  initial?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface PromotionsTabProps {
  syncedCategories: Category[];
  regions: Region[];
  promotionCount: number;
  onPromotionCountChange: (count: number) => void;
}

const PromotionsTab: React.FC<PromotionsTabProps> = ({
  syncedCategories,
  regions,
  promotionCount,
  onPromotionCountChange
}) => {
  const dispatch = useAppDispatch();
  
  // Redux state
  const {
    latestPromotionSetting,
    promotionFormSettings,
    promotionSettingsLoading,
    promotionFormSettingsLoading,
    promotionSettingsError,
    promotionFormSettingsError
  } = useSelector((state: RootState) => state.promotionSettings);

  // Local state for dropdown selections
  const [selectedDropdownCategories, setSelectedDropdownCategories] = useState<DropdownCategory[]>([]);
  const [selectedDropdownRegions, setSelectedDropdownRegions] = useState<DropdownRegion[]>([]);
  const [autoPromotion, setAutoPromotion] = useState<boolean>(true);
  const [defaultFormsPerCategory, setDefaultFormsPerCategory] = useState<number>(1);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  
  // State for selected categories and regions separately
  const [selectedCategories, setSelectedCategories] = useState<Array<{
    id: string;
    category: DropdownCategory;
    formCount: number;
  }>>([]);
  
  const [selectedRegions, setSelectedRegions] = useState<Array<{
    id: string;
    region: DropdownRegion;
    formCount: number;
  }>>([]);

  // Load latest promotion settings on component mount
  useEffect(() => {
    dispatch(fetchLatestPromotionSetting());
    dispatch(fetchPromotionFormSettings({ page: 1, limit: 10 }));
  }, [dispatch]);

  // Update local state when Redux state changes
  useEffect(() => {
    if (latestPromotionSetting) {
      setAutoPromotion(latestPromotionSetting.autoPromotion || false);
      setDefaultFormsPerCategory(latestPromotionSetting.defaultFormsPerCategory || 1);
      
      // Load existing promotion form settings into local state
      if (latestPromotionSetting.promotionFormSettings) {
        const existingCategories: Array<{
          id: string;
          category: DropdownCategory;
          formCount: number;
        }> = [];
        
        const existingRegions: Array<{
          id: string;
          region: DropdownRegion;
          formCount: number;
        }> = [];
        
        latestPromotionSetting.promotionFormSettings.forEach(setting => {
          // If it has a category but no region, add to categories
          if (setting.category && setting.category.id && !setting.region) {
            existingCategories.push({
              id: setting.category.id,
              category: {
                id: setting.category.id,
                name: setting.category.name || "Unknown Category",
                subcategories: []
              },
              formCount: setting.form || 1
            });
          }
          
          // If it has a region but no category, add to regions
          if (setting.region && setting.region.id && !setting.category) {
            existingRegions.push({
              id: setting.region.id,
              region: {
                id: setting.region.id,
                name: setting.region.name || "Unknown Region",
                country: (setting.region as any).country,
                state: (setting.region as any).state,
                city: (setting.region as any).city
              },
              formCount: setting.form || 1
            });
          }
        });
        
        setSelectedCategories(existingCategories);
        setSelectedRegions(existingRegions);
      }
    }
  }, [latestPromotionSetting]);

  // Clear errors when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearPromotionSettingsError());
      dispatch(clearPromotionFormSettingsError());
    };
  }, [dispatch]);

  // Function to add selected categories
  const handleAddCategories = () => {
    selectedDropdownCategories.forEach(dropdownCategory => {
      const newCategory = {
        id: dropdownCategory.id,
        category: dropdownCategory,
        formCount: defaultFormsPerCategory
      };
      
      setSelectedCategories(prev => [...prev, newCategory]);
    });
    
    // Clear dropdown selections
    setSelectedDropdownCategories([]);
  };

  // Function to add selected regions
  const handleAddRegions = () => {
    selectedDropdownRegions.forEach(dropdownRegion => {
      const newRegion = {
        id: dropdownRegion.id,
        region: dropdownRegion,
        formCount: defaultFormsPerCategory
      };
      
      setSelectedRegions(prev => [...prev, newRegion]);
    });
    
    // Clear dropdown selections
    setSelectedDropdownRegions([]);
  };

  // Function to remove category
  const handleRemoveCategory = (id: string) => {
    setSelectedCategories(prev => prev.filter(item => item.id !== id));
  };

  // Function to remove region
  const handleRemoveRegion = (id: string) => {
    setSelectedRegions(prev => prev.filter(item => item.id !== id));
  };

  // Function to update form count for a category
  const handleUpdateCategoryFormCount = (id: string, formCount: number) => {
    setSelectedCategories(prev => 
      prev.map(item => 
        item.id === id ? { ...item, formCount } : item
      )
    );
  };

  // Function to update form count for a region
  const handleUpdateRegionFormCount = (id: string, formCount: number) => {
    setSelectedRegions(prev => 
      prev.map(item => 
        item.id === id ? { ...item, formCount } : item
      )
    );
  };

  const handleSave = async () => {
    setIsSaving(true);
    setSaveMessage(null);
    
    try {
      // First, create or update promotion setting
      let promotionSettingId: string;
      
      if (latestPromotionSetting) {
        // Update existing promotion setting
        const updatedSetting = await dispatch(updatePromotionSettingAction({
          id: latestPromotionSetting.id,
          data: {
            defaultFormsPerCategory: defaultFormsPerCategory,
            autoPromotion: autoPromotion
          }
        })).unwrap();
        promotionSettingId = updatedSetting.id;
      } else {
        // Create new promotion setting
        const newSetting = await dispatch(createPromotionSettingAction({
          defaultFormsPerCategory: defaultFormsPerCategory,
          autoPromotion: autoPromotion
        })).unwrap();
        promotionSettingId = newSetting.id;
      }

      // Identify settings to delete (those in database but not in current selection)
      const formSettingsToDelete: string[] = [];
      
      if (latestPromotionSetting?.promotionFormSettings) {
        latestPromotionSetting.promotionFormSettings.forEach(existingSetting => {
          // Delete orphaned settings (neither category nor region)
          if (!existingSetting.category && !existingSetting.region) {
            console.log(`Found orphaned setting (no category or region): ${existingSetting.id}`);
            formSettingsToDelete.push(existingSetting.id);
            return;
          }
          
          // Check if this is a category setting
          if (existingSetting.category && existingSetting.category.id && !existingSetting.region) {
            const stillSelected = selectedCategories.some(
              item => item.category.id === existingSetting.category?.id
            );
            if (!stillSelected) {
              formSettingsToDelete.push(existingSetting.id);
            }
          }
          
          // Check if this is a region setting
          if (existingSetting.region && existingSetting.region.id && !existingSetting.category) {
            const stillSelected = selectedRegions.some(
              item => item.region.id === existingSetting.region?.id
            );
            if (!stillSelected) {
              formSettingsToDelete.push(existingSetting.id);
            }
          }
        });
      }

      // Delete removed settings
      if (formSettingsToDelete.length > 0) {
        console.log(`Deleting ${formSettingsToDelete.length} promotion form settings...`);
        await Promise.all(
          formSettingsToDelete.map(id => dispatch(deletePromotionFormSettingAction(id)).unwrap())
        );
      }

      // Create promotion form settings for categories and regions separately
      const formSettingsToCreate: PromotionFormSettingCreateDto[] = [];
      const formSettingsToUpdate: Array<{ id: string; data: PromotionFormSettingUpdateDto }> = [];
      
      // Process selected categories
      selectedCategories.forEach(categoryItem => {
        // Check if this category already exists in the database
        const existingSetting = latestPromotionSetting?.promotionFormSettings?.find(
          setting => setting.category?.id === categoryItem.category.id && !setting.region
        );
        
        if (existingSetting) {
          // Update existing setting
          formSettingsToUpdate.push({
            id: existingSetting.id,
            data: {
              form: categoryItem.formCount,
              promotionSetting: { id: promotionSettingId }
            }
          });
        } else {
          // Create new setting
          formSettingsToCreate.push({
            form: categoryItem.formCount,
            promotionSetting: { id: promotionSettingId },
            category: { id: categoryItem.category.id, name: categoryItem.category.name }
          });
        }
      });

      // Process selected regions
      selectedRegions.forEach(regionItem => {
        // Check if this region already exists in the database
        const existingSetting = latestPromotionSetting?.promotionFormSettings?.find(
          setting => setting.region?.id === regionItem.region.id && !setting.category
        );
        
        if (existingSetting) {
          // Update existing setting
          formSettingsToUpdate.push({
            id: existingSetting.id,
            data: {
              form: regionItem.formCount,
              promotionSetting: { id: promotionSettingId }
            }
          });
        } else {
          // Create new setting
          formSettingsToCreate.push({
            form: regionItem.formCount,
            promotionSetting: { id: promotionSettingId },
            region: { id: regionItem.region.id, name: regionItem.region.name }
          });
        }
      });

      // Execute bulk operations
      if (formSettingsToCreate.length > 0) {
        console.log(`Creating ${formSettingsToCreate.length} promotion form settings...`);
        await dispatch(bulkCreatePromotionFormSettingsAction({
          promotionFormSettings: formSettingsToCreate
        })).unwrap();
      }
      
      if (formSettingsToUpdate.length > 0) {
        console.log(`Updating ${formSettingsToUpdate.length} promotion form settings...`);
        await dispatch(bulkUpdatePromotionFormSettingsAction({
          promotionFormSettings: formSettingsToUpdate
        })).unwrap();
      }

      // Refresh data
      await dispatch(fetchLatestPromotionSetting());
      await dispatch(fetchPromotionFormSettings({ page: 1, limit: 10 }));
      
      setSaveMessage("Promotion settings saved successfully!");
      setTimeout(() => setSaveMessage(null), 3000);
      
    } catch (error: any) {
      console.error("Error saving promotion settings:", error);
      setSaveMessage(`Error saving settings: ${error.message || "Unknown error"}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset to latest saved state
    if (latestPromotionSetting) {
      setAutoPromotion(latestPromotionSetting.autoPromotion || false);
      setDefaultFormsPerCategory(latestPromotionSetting.defaultFormsPerCategory || 1);
    }
    
    setSelectedDropdownCategories([]);
    setSelectedDropdownRegions([]);
    setSelectedCategories([]);
    setSelectedRegions([]);
    setSaveMessage(null);
  };
  return (
    <Card className="p-4 sm:p-6">
      <h2 className="text-lg sm:text-xl font-semibold mb-4">Form Promotion Settings</h2>
      <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-6">
        Configure promotional settings to automatically gift forms to new professionals
      </p>
      
      {/* Error Messages */}
      {(promotionSettingsError || promotionFormSettingsError) && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-sm text-red-600">
            {promotionSettingsError || promotionFormSettingsError}
          </p>
        </div>
      )}
      
      {/* Success Message */}
      {saveMessage && (
        <div className={`mb-4 p-3 border rounded-lg ${
          saveMessage.includes("Error") 
            ? "bg-red-50 border-red-200 text-red-600" 
            : "bg-green-50 border-green-200 text-green-600"
        }`}>
          <p className="text-sm">{saveMessage}</p>
        </div>
      )}
      
      <div className="space-y-4 sm:space-y-6">
        <div className="bg-blue-50 p-3 sm:p-4 rounded-lg">
          <div className="flex gap-2 sm:gap-3">
            <Gift className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="text-xs sm:text-sm text-gray-700">
                Form promotions help new professionals get started on the platform by automatically gifting them customer request forms. This feature can be configured globally or per category.
              </p>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-medium mb-3 text-sm sm:text-base">Global Promotion Settings</h3>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg gap-2">
              <div>
                <span className="font-medium text-xs sm:text-sm">Auto-Promotion</span>
                <p className="text-xs text-gray-500 mt-1">Automatically gift forms to new professionals</p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <span className="text-xs sm:text-sm">{autoPromotion ? "Enabled" : "Disabled"}</span>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input 
                    type="checkbox" 
                    className="sr-only peer" 
                    checked={autoPromotion}
                    onChange={(e) => setAutoPromotion(e.target.checked)}
                  />
                  <div className="w-9 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-2 sm:p-3 bg-gray-50 rounded-lg gap-2">
              <div>
                <span className="font-medium text-xs sm:text-sm">Default Forms Per Category</span>
                <p className="text-xs text-gray-500 mt-1">Number of forms to gift per category</p>
              </div>
              <select
                className="mt-2 sm:mt-0 px-2 py-1 sm:px-3 sm:py-1 border rounded-lg text-sm"
                value={defaultFormsPerCategory}
                onChange={(e) => setDefaultFormsPerCategory(parseInt(e.target.value))}
              >
                <option value="1">1 Form</option>
                <option value="2">2 Forms</option>
                <option value="3">3 Forms</option>
                <option value="5">5 Forms</option>
              </select>
            </div>
          </div>
        </div>
        
        <div>
          <h3 className="font-medium mb-3 text-sm sm:text-base">Category-Specific Settings</h3>
          
          {/* Categories Section */}
          <div className="mb-6">
            <div className="mb-3">
              <h4 className="font-medium text-sm mb-2">Categories</h4>
              <div className="flex gap-2 items-start">
                <MultiSelectCategoryDropdown
                  selectedCategories={selectedDropdownCategories}
                  onCategoriesSelect={setSelectedDropdownCategories}
                  alreadySelectedCategories={selectedCategories.map(item => item.category)}
                  placeholder="Select categories..."
                  className="flex-1"
                />
                <Button
                  onClick={handleAddCategories}
                  disabled={selectedDropdownCategories.length === 0}
                  className="text-xs"
                  size="sm"
                >
                  Add Categories
                </Button>
              </div>
            </div>
            
            {selectedCategories.length > 0 ? (
              <div className="space-y-2">
                {selectedCategories.map(categoryItem => (
                  <div key={categoryItem.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Filter className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-sm">{categoryItem.category.name}</span>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-xs sm:text-sm text-gray-600">Forms to gift:</span>
                        <select 
                          className="px-2 py-1 border rounded-lg text-sm"
                          value={categoryItem.formCount}
                          onChange={(e) => handleUpdateCategoryFormCount(categoryItem.id, parseInt(e.target.value))}
                        >
                          <option value="1">1 Form</option>
                          <option value="2">2 Forms</option>
                          <option value="3">3 Forms</option>
                          <option value="5">5 Forms</option>
                          <option value="10">10 Forms</option>
                        </select>
                        <button
                          onClick={() => handleRemoveCategory(categoryItem.id)}
                          className="p-1 hover:bg-red-100 rounded text-red-500 transition-colors"
                          title="Remove category"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">No categories selected</p>
              </div>
            )}
          </div>

          {/* Regions Section */}
          <div>
            <div className="mb-3">
              <h4 className="font-medium text-sm mb-2">Regions</h4>
              <div className="flex gap-2 items-start">
                <MultiSelectAdminRegionDropdown
                  selectedRegions={selectedDropdownRegions}
                  onRegionsSelect={setSelectedDropdownRegions}
                  alreadySelectedRegions={selectedRegions.map(item => item.region)}
                  placeholder="Select regions..."
                  className="flex-1"
                />
                <Button
                  onClick={handleAddRegions}
                  disabled={selectedDropdownRegions.length === 0}
                  className="text-xs"
                  size="sm"
                >
                  Add Regions
                </Button>
              </div>
            </div>
            
            {selectedRegions.length > 0 ? (
              <div className="space-y-2">
                {selectedRegions.map(regionItem => (
                  <div key={regionItem.id} className="p-3 bg-gray-50 rounded-lg">
                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
                      <div className="flex items-center gap-2">
                        <Globe className="h-4 w-4 text-gray-500" />
                        <span className="font-medium text-sm">{regionItem.region.name}</span>
                      </div>
                      <div className="flex items-center gap-2 sm:gap-3">
                        <span className="text-xs sm:text-sm text-gray-600">Forms to gift:</span>
                        <select 
                          className="px-2 py-1 border rounded-lg text-sm"
                          value={regionItem.formCount}
                          onChange={(e) => handleUpdateRegionFormCount(regionItem.id, parseInt(e.target.value))}
                        >
                          <option value="1">1 Form</option>
                          <option value="2">2 Forms</option>
                          <option value="3">3 Forms</option>
                          <option value="5">5 Forms</option>
                          <option value="10">10 Forms</option>
                        </select>
                        <button
                          onClick={() => handleRemoveRegion(regionItem.id)}
                          className="p-1 hover:bg-red-100 rounded text-red-500 transition-colors"
                          title="Remove region"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-sm text-gray-500">No regions selected</p>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div className="pt-4 sm:pt-6 mt-4 sm:mt-6 border-t flex flex-col sm:flex-row justify-end gap-3">
        <Button 
          variant="outline" 
          className="w-full sm:w-auto text-sm"
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button 
          className="w-full sm:w-auto text-sm"
          onClick={handleSave}
          disabled={isSaving || promotionSettingsLoading || promotionFormSettingsLoading}
        >
          {isSaving ? "Saving..." : "Save Promotion Settings"}
        </Button>
      </div>
    </Card>
  );
};

export default PromotionsTab;
